"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { PenLine, Check, MousePointer2 } from "lucide-react";
const line = "block h-2 rounded bg-neutral-200";
const CapabilityCmsEditor = () => {
  const $ = _c(19);
  const [phase, setPhase] = useState(0);
  const [isLg, setIsLg] = useState(false);
  let t0;
  let t1;
  if ($[0] !== phase) {
    t0 = () => {
      const dur = phase === 0 ? 3800 : 3200;
      const t = setTimeout(() => setPhase(_temp), dur);
      return () => clearTimeout(t);
    };
    t1 = [phase];
    $[0] = phase;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const mq = window.matchMedia("(min-width: 1024px)");
      const sync = () => setIsLg(mq.matches);
      sync();
      mq.addEventListener("change", sync);
      return () => mq.removeEventListener("change", sync);
    };
    t3 = [];
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  const editing = phase === 0;
  const cursorLeft = editing ? isLg ? "86%" : "78%" : isLg ? "84%" : "78%";
  let t4;
  if ($[5] !== editing) {
    t4 = <AnimatePresence mode="wait">{editing ? <motion.div key="edit" className="absolute inset-0 p-3 pt-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0,
        scale: 0.96
      }} transition={{
        duration: 0.4
      }}><div className="mb-3 flex items-center gap-2"><span className="grid size-8 shrink-0 place-items-center rounded-lg bg-neutral-100 text-neutral-400"><PenLine size={14} /></span><span className="h-2 flex-1 rounded bg-neutral-200" /></div><div className="space-y-1.5"><span className={`${line} w-4/5`} /><span className={`${line} w-full`} /><motion.span className={line} initial={{
            width: 0,
            opacity: 0
          }} animate={{
            width: ["0%", "0%", "60%", "60%"],
            opacity: [0, 0, 1, 1]
          }} transition={{
            duration: 3.6,
            times: [0, 0.22, 0.5, 1],
            ease: "easeInOut"
          }} /><div className="flex items-center gap-1"><span className={`${line} w-1/2`} /><motion.span className="h-2.5 w-0.5 shrink-0 bg-card-foreground" animate={{
              opacity: [1, 0, 1]
            }} transition={{
              duration: 0.7,
              repeat: Infinity
            }} /></div></div><div className="absolute inset-x-3 bottom-3 flex items-center justify-between"><span className="inline-flex items-center gap-1 rounded-lg bg-neutral-100 px-2.5 py-1.5 text-[10px] font-black uppercase tracking-wide text-neutral-500"><PenLine size={11} /> Editing</span><span className="inline-flex rounded-lg bg-card-foreground px-3 py-1.5 text-[10px] font-black uppercase tracking-wide text-white">Publish</span></div></motion.div> : <motion.div key="pub" className="absolute inset-0 grid place-items-center p-3" initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.4
      }}><div className="flex flex-col items-center gap-2"><motion.span className="grid size-11 place-items-center rounded-full bg-card-foreground text-white" initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            delay: 0.15,
            type: "spring",
            stiffness: 300,
            damping: 18
          }}><Check size={22} /></motion.span><span className="text-[11px] font-black uppercase tracking-wide text-neutral-700">Published</span></div><span className="absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-lg border border-black/10 bg-white px-2.5 py-1.5 text-[10px] font-black uppercase tracking-wide text-neutral-600"><PenLine size={11} /> New content</span></motion.div>}</AnimatePresence>;
    $[5] = editing;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== cursorLeft) {
    t5 = {
      left: cursorLeft,
      top: "84%"
    };
    $[7] = cursorLeft;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      opacity: 0,
      scale: 0.6
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      opacity: 1,
      scale: [0.6, 1, 0.82, 1]
    };
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      duration: 0.6,
      times: [0, 0.55, 0.78, 1],
      ease: "easeOut"
    };
    t9 = <MousePointer2 size={15} className="fill-white" />;
    $[11] = t8;
    $[12] = t9;
  } else {
    t8 = $[11];
    t9 = $[12];
  }
  let t10;
  if ($[13] !== phase || $[14] !== t5) {
    t10 = <motion.div key={phase} className="pointer-events-none absolute z-10 -ml-1 -mt-1 text-card-foreground" style={t5} initial={t6} animate={t7} transition={t8}>{t9}</motion.div>;
    $[13] = phase;
    $[14] = t5;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t4) {
    t11 = <div className="relative mt-auto h-52 overflow-hidden rounded-2xl border border-black/5 bg-white">{t4}{t10}</div>;
    $[16] = t10;
    $[17] = t4;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  return t11;
};
export default CapabilityCmsEditor;
function _temp(p) {
  return (p + 1) % 2;
}